import { Hint, Tooltip } from '@systemeio/ui'
import SettingsLayout from 'modules/profile/components/settings-layout'
import { settingsMenuLocoKeys } from 'modules/profile/constants/settings-menu-loco-keys'
import WebhookCreateModal from 'modules/profile/webhooks/components/create-modal'
import WebhookModal from 'modules/profile/webhooks/components/webhook-modal'
import { eventTypeTitleLocoKeys } from 'modules/profile/webhooks/constants/event-type-translation-dictionary'
import { WebhookSettingsLoco } from 'modules/profile/webhooks/constants/webhook-settings-loco'
import { useWebhookSettings } from 'modules/profile/webhooks/hooks/use-webhook-settings'
import { useWebhookSettingsApi } from 'modules/profile/webhooks/hooks/use-webhook-settings-api'
import { WebhookInterface } from 'modules/profile/webhooks/types/webhook-settings-interface'
import React, { useState } from 'react'
import ListingLayout from 'shared/components/listing-layout'
import { Table } from 'shared/components/table/table'
import { ColumnDefinitionInterface } from 'shared/components/table/types/column-interface'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import { DataWithPaginationInterface } from 'shared/types/pagination-interface'
import { getBaseServerSideProps } from 'shared/utils/get-base-server-side-props'
import TransLoco from 'shared/components/trans-loco'
import { getWebhooksInfoLink } from 'modules/profile/webhooks//utils/get-webhooks-info-link'

const WebhookSettings = () => {
  const { t } = useLocoTranslation()

  const [current, setCurrent] = useState<WebhookInterface | undefined>()

  const { webhooks, mutate, isLoading } = useWebhookSettings()
  const { remove } = useWebhookSettingsApi()

  const columns: ColumnDefinitionInterface<WebhookInterface>[] = [
    {
      key: 'name',
      header: t('global.name'),
      widthPercent: 20,
    },
    {
      key: 'url',
      header: t('global.url'),
      widthPercent: 40,
    },
    {
      key: 'subscriptions',
      header: t('dashboard.settings.webhook_settings.subscription_count'),
      onRender: ({ subscriptions }) => {
        const enabledEvents = subscriptions.filter(s => !s.disabledReason)
        const eventsList = enabledEvents.map(s => t(eventTypeTitleLocoKeys[s.event])).join(', ')

        if (!eventsList) {
          return null
        }

        return (
          <Tooltip label={eventsList} className="flex gap-2">
            <div className="w-[50%] overflow-hidden text-ellipsis">{eventsList}</div>
            <div>
              {t('dashboard.settings.webhook_settings.subscription_count_caption', {
                number: enabledEvents.length.toString(),
              })}
            </div>
          </Tooltip>
        )
      },
      widthPercent: 40,
    },
  ]

  const data =
    webhooks &&
    ({
      items: webhooks,
      hasMore: false,
    } as DataWithPaginationInterface<WebhookInterface>)

  const handleSaveWebhook = async (webhook: WebhookInterface) =>
    await mutate(data => [webhook].concat(data ?? []), false)

  const handleSaveEditWebhook = async (webhook: WebhookInterface) =>
    await mutate(data => data?.map(wh => (wh.id === webhook.id ? webhook : wh)), false)

  const handleRemove = async (webhook: WebhookInterface) => {
    await remove(webhook.id)
    await mutate(data => data?.filter(wh => wh.id !== webhook.id), false)
  }

  return (
    <SettingsLayout
      needSave={false}
      titles={[{ caption: t(settingsMenuLocoKeys.webhooks), isStatic: true }]}
      isFetching={isLoading}
    >
      <div className={'w-full lg:max-w-[65%] xl:max-w-[75%]'}>
        <ListingLayout
          data={data}
          counter={webhooks && { count: webhooks.length }}
          title={
            <Hint
              label={
                <TransLoco
                  t={t}
                  i18nKey={'dashboard.user_settings.webhooks.title_hint'}
                  components={{
                    a: (
                      <a
                        rel="noreferrer"
                        target={'_blank'}
                        className="primary-link w-full"
                        href={getWebhooksInfoLink()}
                      />
                    ),
                  }}
                />
              }
            >
              <h1 className={'flex h-[42px] items-center whitespace-nowrap'}>
                {t('dashboard.user_settings.webhooks.title_plural')}
              </h1>
            </Hint>
          }
          type="compact"
          customHeaderElement={<WebhookCreateModal onSave={handleSaveWebhook} />}
        >
          <Table
            data={data}
            columns={columns}
            caption={t('dashboard.user_settings.webhooks.caption')}
            actions={{
              remove: {
                onChange: handleRemove,
              },
              openLink: [
                {
                  isNext: true,
                  name: 'view',
                  caption: t('dashboard.user_settings.webhooks.actions.view_logs'),
                  setHref: webhook => `/profile/webhook-settings/${webhook.id}`,
                },
              ],
              custom: [
                {
                  name: 'edit',
                  caption: t('global.edit'),
                  onActionClick: async webhook => setCurrent(webhook),
                },
              ],
            }}
          />
        </ListingLayout>
      </div>
      <WebhookModal
        isOpen={!!current}
        onClose={() => setCurrent(undefined)}
        webhook={current}
        onSave={handleSaveEditWebhook}
      />
    </SettingsLayout>
  )
}

export const getServerSideProps = getBaseServerSideProps(WebhookSettingsLoco, 'global.settings')

export default WebhookSettings
